import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Star, Eye, EyeOff, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  const fetchListings = () => {
    setLoading(true);
    axios.get(`${API}/admin/listings`, { headers: getAuthHeaders(), withCredentials: true })
      .then(res => setListings(res.data))
      .catch(() => toast.error("Failed to load listings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchListings(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/listings/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Listing deleted");
      setDeleteId(null);
      fetchListings();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDuplicate = async (listing) => {
    try {
      const dup = { ...listing };
      delete dup.id;
      delete dup.created_at;
      delete dup.updated_at;
      dup.title = `${listing.title} (Copy)`;
      dup.slug = "";
      dup.airbnb_url = "";
      dup.vrbo_url = "";
      dup.is_published = false;
      await axios.post(`${API}/admin/listings`, dup, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Listing duplicated as draft");
      fetchListings();
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  const togglePublished = async (listing) => {
    try {
      await axios.put(`${API}/admin/listings/${listing.id}`, {
        ...listing,
        is_published: !listing.is_published
      }, { headers: getAuthHeaders(), withCredentials: true });
      fetchListings();
    } catch {
      toast.error("Failed to update");
    }
  };

  const toggleFeatured = async (listing) => {
    try {
      await axios.put(`${API}/admin/listings/${listing.id}`, {
        ...listing,
        is_featured: !listing.is_featured
      }, { headers: getAuthHeaders(), withCredentials: true });
      fetchListings();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-listings" className="px-6 md:px-12 lg:px-24 py-8">
      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center" data-testid="delete-modal">
          <div className="bg-white border border-[#E2E8F0] p-8 max-w-sm w-full mx-4 rounded-lg shadow-lg">
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#1A1A2E] mb-3">Delete Listing?</h3>
            <p className="text-[#64748B] text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteId)}
                data-testid="confirm-delete"
                className="bg-[#D94E4E] text-white px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-red-600 transition-colors rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteId(null)}
                data-testid="cancel-delete"
                className="border border-[#E2E8F0] text-[#1A1A2E] px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-[#F4F6FA] transition-colors rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-['DM_Serif_Display'] text-3xl sm:text-4xl text-[#1A1A2E] mb-1">Listings</h1>
          <p className="text-[#94A3B8] text-sm">{listings.length} total listings</p>
        </div>
        <Link
          to="/admin/listings/new"
          data-testid="add-listing-btn"
          className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-6 py-2.5 uppercase tracking-widest text-xs font-semibold inline-flex items-center gap-2 rounded"
        >
          <Plus className="w-4 h-4" /> Add Listing
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#64748B] mb-4">No listings yet.</p>
          <Link to="/admin/listings/new" className="text-[#32A334] text-sm uppercase tracking-[0.15em]">Create your first listing</Link>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#E2E8F0] rounded-lg" data-testid="listings-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-[#F4F6FA]">
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal">Image</th>
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal">Title</th>
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal hidden md:table-cell">Status</th>
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal hidden lg:table-cell">Featured</th>
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal hidden lg:table-cell">Details</th>
                <th className="py-3 px-4 text-xs uppercase tracking-[0.15em] text-[#94A3B8] font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr key={listing.id} className="border-b border-[#E2E8F0] hover:bg-[#F4F6FA]/50 transition-colors" data-testid={`listing-row-${listing.id}`}>
                  <td className="py-3 px-4">
                    <div className="w-16 h-12 overflow-hidden bg-[#F4F6FA] rounded">
                      {listing.featured_image && (
                        <img src={listing.featured_image} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-[#1A1A2E] text-sm font-medium">{listing.title}</p>
                    <p className="text-[#94A3B8] text-xs">{listing.location}</p>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <button
                      onClick={() => togglePublished(listing)}
                      data-testid={`toggle-published-${listing.id}`}
                      className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] px-3 py-1 border transition-colors rounded ${
                        listing.is_published
                          ? "border-[#2D8A65]/30 text-[#2D8A65] bg-[#2D8A65]/5"
                          : "border-[#E2E8F0] text-[#94A3B8]"
                      }`}
                    >
                      {listing.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {listing.is_published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="py-3 px-4 hidden lg:table-cell">
                    <button
                      onClick={() => toggleFeatured(listing)}
                      data-testid={`toggle-featured-${listing.id}`}
                      className={`transition-colors ${listing.is_featured ? "text-[#32A334]" : "text-[#CBD5E1] hover:text-[#32A334]"}`}
                    >
                      <Star className="w-4 h-4" fill={listing.is_featured ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td className="py-3 px-4 hidden lg:table-cell text-[#64748B] text-xs">
                    {listing.bedrooms}bd / {listing.bathrooms}ba / {listing.sleeps}sl
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/properties/${listing.slug}`}
                        target="_blank"
                        data-testid={`preview-${listing.id}`}
                        className="text-[#94A3B8] hover:text-[#32A334] transition-colors p-1.5"
                        title="Preview"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDuplicate(listing)}
                        data-testid={`duplicate-${listing.id}`}
                        className="text-[#94A3B8] hover:text-[#32A334] transition-colors p-1.5"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/listings/${listing.id}/edit`)}
                        data-testid={`edit-${listing.id}`}
                        className="text-[#94A3B8] hover:text-[#32A334] transition-colors p-1.5"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteId(listing.id)}
                        data-testid={`delete-${listing.id}`}
                        className="text-[#94A3B8] hover:text-[#D94E4E] transition-colors p-1.5"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
